import { API, ApiArgs } from '@/api/API'
import {
  apiEntities,
  DateTimeTriggerDetail,
  EventDetails,
  firstEntity,
  linkTemplateToMetadata,
  MessageRuleDetail,
  toLinkedGroups,
} from '@/api/Helpers'
import NotFoundError from '@/errors/NotFoundError'
import { TGQueryParam } from '@/helpers/TicketGroupHelpers'
import moment from 'moment-timezone'

/**
 * TODO Implement this as a cleaner alternative to getEvents().
 */
export function fetchAvailableEvents(options): Promise<EventDetails[]> {
  return Promise.reject()
}

type Entities = 'meta' | 'venue' | 'ticket_group' | 'ticket_type' | 'event_template' | 'message_rule'
export type EventApiResponse = ApiResponse<Entities>

export function fetchEventDetails(id: string, tgIDs?: TGQueryParam): Promise<EventDetails> {
  const tgVisibilityTypes = ['public_browsable', 'public_member_only']

  if (tgIDs) {
    // Include link-only ticket groups.
    tgVisibilityTypes.push('public_link_only')
  }

  const args: ApiArgs = {
    query: {
      'hidden_type._in': 'public_browsable,public_link_only,public_member_only',
      'ticket_group.id._in': tgIDs,
      'ticket_group.hidden_type._in': tgVisibilityTypes,
    },
    embed: 'meta, venue, ticket_group, ticket_group.meta, ticket_type, message_rule',
  }

  return API.getCached<Entities>(`events/${id}`, args)
    .catch((error) => {
      throw handleError(error, id, tgIDs)
    })
    .then(toEventDetails)
}

export function fetchDonationTGAndEvent(tgid: string): Promise<EventDetails> {
  const params: ApiArgs = {
    // Omit message_rule for Donation TGs. Message rules aren't supported in this context.
    embed: 'meta, event_template, event_template.venue, event_template.meta, ticket_type',
    query: { handler: 'donation' },
  }

  return API.getCached<Entities>(`ticket_group/${tgid}`, params)
    .then(toEventDetails)
    .then((event) => {
      if (!event.id) {
        throw new Error('No event_template found for donation ticket group')
      }

      return event
    })
}

export function toMessageRuleDetails(rules: MessageRule[], timezone: string): MessageRuleDetail[] {
  return rules.map((rule) => ({
    ...rule,
    triggers: {
      ...rule.triggers,
      dateOrSession: rule.triggers.date_or_session?.map((trigger) => {
        const result: DateTimeTriggerDetail = {
          ...trigger,
        }

        if (trigger.date_range) {
          result.dateInterval = {
            start: moment.tz(trigger.date_range.start, timezone),
            // Add 24 hours so that it is the end of that day.
            end: moment.tz(trigger.date_range.end, timezone).add(24, 'hours'),
          }
        }

        return result
      }),
    },
  }))
}

function toEventDetails(response: EventApiResponse): EventDetails {
  const [template, venue] = firstEntity(response, 'event_template', 'venue')
  const entities = apiEntities(response)
  const result = linkTemplateToMetadata(template, entities.meta)
  return {
    ...result,
    venue,
    ticketTypes: entities.ticket_type,
    ticketGroups: toLinkedGroups(response),
    messageRules: entities.message_rule ? toMessageRuleDetails(entities.message_rule, venue.timezone) : [],
  }
}

function handleError(error, ...args) {
  const status = error?.response?.status
  if (status === 404 || status === 400) {
    return new NotFoundError('event_template', ...args)
  } else {
    return error
  }
}
